// Share a dev server: a public link to one port (for a phone, a teammate or webhooks) that
// works for anyone with it until it expires or is revoked, with a QR code for phones. Opened
// from the Share button in a preview's bar (canvas 4b "Share").

import { IconCheck, IconCopy, IconEye, IconEyeOff, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { QR } from "./QR";
import { shares, type Share } from "../api";
import { copyText } from "./sheets";
import { Button, Ic, IconSwap, Meta, Pix, Segmented } from "./ds";
import { REDUCED_MS, reducedMotion } from "./motion";
import "./share.css";

const DURATIONS: [string, string][] = [
  ["60", "1 hour"],
  [String(24 * 60), "1 day"],
  [String(7 * 24 * 60), "7 days"],
];

function until(iso: string) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const m = Math.round(ms / 60000);
  if (m <= 60) return `Expires in ${m} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `Expires in ${h} h` : `Expires in ${Math.round(h / 24)} days`;
}

function ShareRow({ s, onRevoked }: { s: Share; onRevoked: () => void }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="share-item" data-testid="share-item" data-share-id={s.id}>
      <div className="share-link">
        <code data-testid="share-url">{s.url}</code>
        <Button
          size="sm"
          className="share-copy"
          data-copied={copied ? "" : undefined}
          onClick={() =>
            void copyText(s.url).then((ok) => {
              if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }
            })
          }
        >
          <IconSwap on={copied} off={IconCopy} to={IconCheck} size={14} stroke={1.5} onStroke={2} />
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="share-meta">
        <span>
          {until(s.expires)}
          {s.protected ? " · password" : ""}
        </span>
        <span>·</span>
        <a href={s.url} target="_blank" rel="noopener">
          Open
        </a>
        <span className="share-grow" />
        <Button
          size="xs"
          tone="danger"
          className="share-revoke"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await shares.revoke(s.id);
            onRevoked();
          }}
          data-testid="share-revoke"
        >
          Revoke
        </Button>
      </div>
    </div>
  );
}

/** The password field: the input and its show/hide button share one 32px box. */
function PasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="share-field">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={shown ? "text" : "password"}
        autoComplete="new-password"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        id="share-pw"
        data-testid="share-password"
      />
      <Button
        variant="icon"
        size="sm"
        radius={6}
        aria-label={shown ? "Hide password" : "Show password"}
        aria-pressed={shown}
        data-testid="share-password-toggle"
        onMouseDown={(e) => e.preventDefault()} // the field keeps focus (and its caret)
        onClick={() => setShown((x) => !x)}
      >
        <IconSwap on={shown} off={IconEye} to={IconEyeOff} size={14} />
      </Button>
    </div>
  );
}

export function ShareDialog({
  port,
  process,
  onClose,
}: {
  port: number;
  process?: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [minutes, setMinutes] = useState("60");
  const [password, setPassword] = useState("");
  const [list, setList] = useState<Share[] | null>(null);
  const [fresh, setFresh] = useState<Share | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await shares.list();
    if (r.ok) setList((r.data.shares ?? []).filter((s) => s.port === port));
    else setError(r.error);
  };
  useEffect(() => {
    ref.current?.showModal();
    void load();
  }, []);

  const create = async () => {
    setBusy(true);
    setError(null);
    const r = await shares.create(port, Number(minutes), password || undefined);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setFresh(r.data);
    setPassword("");
    void load();
  };

  const others = list ? list.filter((s) => s.id !== fresh?.id) : [];
  // Closed (Esc, ×, the backdrop): the dialog plays its exit (share.css, --dur-exit-lg) before it goes.
  const leave = () => window.setTimeout(onClose, reducedMotion() ? REDUCED_MS : 180);

  return (
    <dialog
      ref={ref}
      className="share-dialog"
      onClose={leave}
      aria-labelledby="share-title"
      data-testid="share-dialog"
    >
      <div className="share-head">
        <h2 id="share-title">
          Share <span className="share-port">:{port}</span>
          {process ? <Meta>{process}</Meta> : null}
        </h2>
        <Button
          variant="icon"
          size="sm"
          aria-label="Close"
          onClick={() => ref.current?.close()}
          data-testid="share-close"
        >
          <Ic icon={IconX} />
        </Button>
      </div>
      <p className="share-lead">
        A public link to this dev server, for your phone, a teammate or
        webhooks. Anyone with it can open it until it expires.
      </p>

      {fresh && (
        <div className="share-fresh" data-testid="share-fresh">
          <QR text={fresh.url} />
          <div className="share-fresh-side">
            <ShareRow
              s={fresh}
              onRevoked={() => (setFresh(null), void load())}
            />
            <span className="share-scan">
              Scan the code to open it on your phone.
            </span>
          </div>
        </div>
      )}

      <form
        className="share-form"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <div className="share-ctl">
          <span className="share-label">Works for</span>
          <div className="share-dur" data-testid="share-duration">
            <Segmented
              label="Works for"
              value={minutes}
              onChange={setMinutes}
              items={DURATIONS.map(([value, label]) => ({ value, label }))}
            />
          </div>
        </div>
        <div className="share-ctl">
          <label className="share-label" htmlFor="share-pw">
            Password (optional)
          </label>
          <PasswordField value={password} onChange={setPassword} />
        </div>
        <Button
          type="submit"
          variant={fresh ? "secondary" : "primary"}
          className="share-create"
          disabled={busy}
          data-testid="share-create"
        >
          {busy ? "Creating…" : fresh ? "Create another link" : "Create link"}
        </Button>
      </form>
      {error && (
        <p className="share-error" role="alert">
          {error}
        </p>
      )}

      {others.length > 0 && (
        <div className="share-list">
          <div className="share-list-head">
            <Pix>Active links for :{port}</Pix>
          </div>
          {others.map((s) => (
            <ShareRow key={s.id} s={s} onRevoked={() => void load()} />
          ))}
        </div>
      )}
    </dialog>
  );
}
